<template>
    <section class="a-compliance-card">
        <div class="wrapper">
            <div class="image-wrapper">
                <a-picture :link="image" :alt="name" class="image" />
            </div>
            <div class="content">
                <a-label
                    v-if="label"
                    v-bind="label"
                    class="label"
                    :size="label.size || 's'"
                >
                    {{ label.text }}
                </a-label>
                <span class="title">{{ name }}</span>
                <p class="description">
                    {{ description }}
                </p>
                <div v-if="!expand" class="actions">
                    <a
                        v-if="pdfButton"
                        :class="{ 'in-progress': isGeneratingPdf }"
                        class="download-detailed-summary a-link-static"
                        :data-has-no-href="true"
                        @click="exportPdf"
                    >
                        <span>{{ pdfButton?.text }}</span>
                        <a-glyph
                            name="pdf"
                            class="icon"
                            :class="{ animate: isGeneratingPdf }"
                        />
                    </a>
                    <a-link
                        v-if="toggleButton"
                        :text="toggleButton?.text"
                        size="paragraph"
                        class="toggle"
                        accent
                        :glyph="toggleButton?.glyph"
                        @click.prevent="onToggle"
                    />
                    <div v-if="isError" class="erorr">
                        {{ buttons.pdfError?.text }}
                    </div>
                </div>
                <div v-else class="actions">
                    <a-link
                        v-bind="expand"
                        size="paragraph"
                        class="learn-more"
                        accent
                        :glyph="expand?.glyph ?? 'i-long-arrow-right--16'"
                    />
                </div>
            </div>
        </div>
        <div v-if="isExpanded && expandable" class="expandable">
            <span v-if="expandable.title" class="title">{{ expandable.title }}</span>
            <a-dangerous-html
                v-if="expandable.lead"
                tag="p"
                :content="expandable.lead"
                class="lead"
            />
            <span v-if="expandable.tableHeading" class="table-heading">{{ expandable.tableHeading }}</span>
            <div v-if="expandable.table" class="table-wrap">
                <table class="compliance-mapping-table">
                    <thead>
                        <th v-for="(head, i) in expandable.table.head" :key="`heading_${head}_${i}`">
                            {{ head }}
                        </th>
                    </thead>
                    <tbody>
                        <tr v-for="(row, i) in expandable.table.rows" :key="`row_${i}`">
                            <td v-for="(rowData, j) in row" :key="`column_${j}`">
                                <a-dangerous-html
                                    tag="span"
                                    :content="rowData.text"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <a-dangerous-html
                v-if="expandable?.disclaimer || cardsDisclaimer"
                class="disclaimer"
                :content="expandable?.disclaimer || cardsDisclaimer"
            />
            <div v-if="!expand" class="actions">
                <a
                    v-if="pdfButton"
                    :class="{ 'in-progress': isGeneratingPdf }"
                    class="download-detailed-summary a-link-static"
                    :data-has-no-href="true"
                    @click="exportPdf"
                >
                    <span>{{ pdfButton?.text }}</span>
                    <a-glyph
                        name="pdf"
                        class="icon"
                        :class="{ animate: isGeneratingPdf }"
                    />
                </a>
                <a-link
                    v-if="toggleButton"
                    :text="toggleButton?.text"
                    size="paragraph"
                    class="toggle"
                    accent
                    :glyph="toggleButton?.glyph"
                    @click.prevent="onToggle"
                />
                <div v-if="isError" class="erorr">
                    {{ buttons.pdfError?.text }}
                </div>
            </div>
            <div v-else class="actions">
                <a-link
                    v-bind="expand"
                    size="paragraph"
                    class="learn-more"
                    accent
                    :glyph="expand?.glyph ?? 'i-long-arrow-right--16'"
                />
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALabel from '@core/components/label/label.vue';
import ALink from '@core/components/link/link.vue';
import APicture from '@core/components/picture/picture.vue';
import formHelper from '@utils/form.js';

const props = defineProps({
    label: { type: Object, default: undefined },
    name: { type: String, default: '' },
    description: { type: String, default: '' },
    image: { type: String, default: '' },
    regions: { type: Array, default: () => [] },
    industries: { type: Array, default: () => [] },
    expand: { type: Object, default: null },
    framework: { type: String, default: '' },
    cardsDisclaimer: { type: String, default: '' },
    buttons: { type: Object, default: null },
    expandable: { type: Object, default: null },
    pdfFooter: { type: Object, default: null },
});
const store = useStore();
const isExpanded = ref(false);
const isGeneratingPdf = ref(false);
const isError = ref(false);
const pdfButton = computed(() => isGeneratingPdf.value ? props.buttons.loadingPdf : props.buttons.generatePdf);
const toggleButton = computed(() => isExpanded.value ? props.buttons.collapse : props.buttons.expand);
const config = computed(() => store.getters['config/$config']);

const onToggle = () => {
    isExpanded.value = !isExpanded.value;
};

const exportPdf = async() => {
    const generatorBaseURL = config.value.env.HEAD_SITE_MAIN_PUBLIC_BASE_URL_SERVICE_API_CLIENT;

    if (isGeneratingPdf.value || isError.value) return;
    isGeneratingPdf.value = true;

    formHelper.sendDataLayer({
        event: 'Acronis',
        eventCategory: 'Interactions',
        eventAction: 'link',
        eventLabel: 'pdf export',
        eventContent: 'Compliance Mapping PDF',
    });

    try {
        const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const fileName = `Your-${props.name.replace(' ', '-')}-Mapping-from-Acronis_${timestamp}.pdf`;

        const data = {
            ...props.expandable,
            ...props.pdfFooter,
            ...props.cardsDisclaimer,
        };

        const body = {
            templateId: 'compliance-mapping.v1',
            format: 'pdf',
            data,
        };

        const generatorURL = `${generatorBaseURL}/remote-render/compliance-mapping`;

        const response = await fetch(generatorURL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } else {
            isError.value = true;
        }
    } catch {
        isError.value = true;
        return;
    } finally {
        isGeneratingPdf.value = false;
    }
};
</script>

<style scoped lang="postcss">
.a-compliance-card {
    width: 100%;
    border: 1px solid var(--av-brand-secondary-light);
    box-shadow: var(--av-shadow-small);
    border-radius: 4px;
    background: var(--av-inversed-primary);
    color: var(--av-nav-primary);
    .label {
        margin-bottom: 8px;
    }
    .wrapper {
        display: flex;
        flex-flow: row wrap;
        justify-content: center;
        align-items: center;
        gap: 24px;
        @media (--viewport-tablet) {
            justify-content: space-between;
            gap: 0;
        }
        .image-wrapper {
            @media (--viewport-tablet) {
                @mixin colls 4;
                padding-inline-end: 8px;
                :deep(img) {
                    max-height: 190px;
                }
            }
        }
        .content {
            padding: 24px;
            @media (--viewport-tablet) {
                @mixin colls 8;
            }
            .title {
                @mixin title-accent;
                display: block;
                margin-bottom: 16px;
            }
            .description {
                @mixin paragraph;
                color: var(--av-fixed-secondary);
                margin-bottom: 24px;
            }

        }
    }
    .expandable {
        padding: 24px;
        .title {
            display: block;
            @mixin title-accent;
            margin-bottom: 16px;
        }
        .lead {
            @mixin paragraph;
            color: var(--av-fixed-secondary);
            margin-bottom: 24px;
        }
        .table-heading {
            display: block;
            @mixin title-accent;
            margin-bottom: 16px;
        }
        .table-wrap {
            max-width: calc(100vw - 82px);
            margin-bottom: 16px;
            @media (--viewport-tablet) {
                max-width: calc(100vw - 114px);
            }
            overflow-x: auto;
            border: 1px solid var(--av-nav-primary);
            border-radius: 8px;
        }
        .disclaimer {
            @mixin body;
            color: var(--av-fixed-secondary);
            margin-bottom: 24px;
            font-style: italic;
        }
        table.compliance-mapping-table {
            border-collapse: collapse;
            overflow: hidden;
            @media (--viewport-desktop) {
                table-layout: fixed;
                width: 100%;
            }
            thead th {
                @mixin lead-accent;
                white-space: nowrap;
                padding: 8px 16px;
                background: var(--av-nav-primary);
                color: var(--av-inversed-primary);
            }
            tbody td {
                @mixin body;
                padding: 16px;
                border-right: 1px solid var(--av-solid-brand-secondary-light);
                border-bottom: 1px solid var(--av-solid-brand-secondary-light);
            }
            tbody tr {
                td:last-child {
                    border-right: none;
                }
                &:last-child {
                    border-bottom: none;
                }
            }
        }
    }
    .actions {
        display: flex;
        flex-flow: column;
        gap: 16px;
        justify-content: space-between;
        @media (--viewport-desktop) {
            flex-flow: row wrap;
            gap: 0px;
        }
        .erorr {
            width: 100%;
            color: var(--av-fixed-danger);
        }
        .toggle {
            :deep(.a-link__content) {
                background: none;
            }
        }
        .learn-more {
            margin-inline-start: auto;
        }
        .download-detailed-summary {
            @mixin paragraph-accent;
            display: flex;
            align-items: center;
            gap: 8px;
            &.in-progress {
                opacity: 0.6;
                pointer-events: none;
            }
            .icon {
                &.animate {
                    animation: vroi-pdf-rotate 2s linear infinite;
                    transform-origin: center;
                    transform-style: preserve-3d;
                }
            }
        }
    }
}

@keyframes vroi-pdf-rotate {
    0% { transform:rotate(0deg);}
    100% { transform:rotate(360deg);}
}
</style>
